// Analytics event names. Kept in one place so a typo in a callsite is a compile error rather than
// a silently-misnamed event that only shows up as a gap in the dashboards weeks later.
//
// Naming: snake_case, past tense for things that happened. The native host forwards these verbatim,
// so renaming one splits its history - treat a name as permanent once it has shipped.
//
// User identity and session lifecycle are deliberately absent: the native app already tracks those.
// Nothing here may carry a profile id, name or e-mail address.
export const ANALYTICS_EVENTS = {
    // 1. Funnel: getting from the home screen into a running match.
    PLAY_MODE_SELECTED: 'play_mode_selected',
    GAME_TYPE_SELECTED: 'game_type_selected',
    MATCH_CREATED: 'match_created',
    MATCH_CREATE_FAILED: 'match_create_failed',
    MATCH_SETTINGS_CHANGED: 'match_settings_changed',
    PLAYERS_SELECTED: 'players_selected',
    STARTER_SELECTED: 'starter_selected',
    MATCH_SETUP_ABANDONED: 'match_setup_abandoned',

    // 2. Lobby: an online match hangs on a second person showing up, which fails for reasons
    // outside the app (invite not seen, waited too long). Without these, "online doesn't work"
    // is unfalsifiable.
    LOBBY_OPENED: 'lobby_opened',
    LOBBY_PLAYER_ADDED: 'lobby_player_added',
    LOBBY_PLAYER_REMOVED: 'lobby_player_removed',
    MATCH_INVITE_SENT: 'match_invite_sent',
    MATCH_INVITE_ACCEPTED: 'match_invite_accepted',
    MATCH_INVITE_DECLINED: 'match_invite_declined',
    MATCH_CANCELLED: 'match_cancelled',

    // 3. Gameplay. Aggregated per leg/set/match on purpose: a dart-level event would mean
    // hundreds of events per match, which is noise and cost for no extra answer.
    MATCH_STARTED: 'match_started',
    LEG_FINISHED: 'leg_finished',
    SET_FINISHED: 'set_finished',
    MATCH_FINISHED: 'match_finished',
    MATCH_ABANDONED: 'match_abandoned',
    HIGHLIGHT_SCORED: 'highlight_scored',
    TURN_EDITED: 'turn_edited',
    REMATCH_STARTED: 'rematch_started',

    // 4. Setups and products. The only group that touches revenue: everything else measures
    // whether people enjoy the app, this measures whether the app sells anything.
    SETUP_CREATED: 'setup_created',
    SETUP_EDITED: 'setup_edited',
    SETUP_DELETED: 'setup_deleted',
    SETUP_APPLIED_TO_MATCH: 'setup_applied_to_match',
    PRODUCT_SEARCHED: 'product_searched',
    PRODUCT_SELECTED: 'product_selected',
    GAMEON_CLOSED: 'gameon_closed',

    // 5. Social and profile. Friends are retention: a player with opponents in their list comes
    // back, a solo player stops.
    FRIEND_SEARCHED: 'friend_searched',
    FRIEND_REQUEST_SENT: 'friend_request_sent',
    FRIEND_REQUEST_ACCEPTED: 'friend_request_accepted',
    FRIEND_REQUEST_DECLINED: 'friend_request_declined',
    FRIEND_REQUEST_CANCELLED: 'friend_request_cancelled',
    FRIEND_REMOVED: 'friend_removed',
    PROFILE_EDITED: 'profile_edited',
    AVATAR_UPLOADED: 'avatar_uploaded',
    STATS_FILTER_CHANGED: 'stats_filter_changed',
    NOTIFICATION_OPENED: 'notification_opened',
} as const

// A turn worth reporting on its own. 140+ is the threshold the darts world itself treats as
// notable, and it keeps highlight_scored rare enough to stay a signal rather than a per-turn feed.
export const HIGHLIGHT_SCORE_THRESHOLD = 140

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]
